import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { getConnection } from "@/lib/integrations.functions";
import {
  listJobs, listIntegrationEvents,
  testConnection as testConnectionFn,
  updateConnectionAlias,
} from "@/lib/integrations.functions";
import {
  enableConnection, disableConnection, archiveConnection, deleteConnection,
  listConnectionLifecycleAudit,
} from "@/lib/integrations.functions";
import { PROVIDER_FIELDS, PROVIDER_CATEGORY } from "@/lib/integrations/categories";
import { runCarrierCertification, listCertifications } from "@/lib/carrier-certification.functions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BEHAVIOR_REGISTRY, type BehaviorGroup, type BehaviorControl } from "@/lib/connections/behavior-registry";
import {
  listConnectionBehavior, setConnectionBehavior, resetConnectionBehavior,
  explainConnectionBehavior, type ResolvedBehavior,
} from "@/lib/connections/behavior.functions";
import {
  ChevronLeft, ChevronDown, ChevronRight, Sparkles, Settings2,
  Activity as ActivityIcon, Info, AlertTriangle,
  ArrowDownToLine, ArrowUpFromLine, CheckCircle2, XCircle, Clock,
  HelpCircle, RotateCcw, ExternalLink,
} from "lucide-react";
import {
  listConnectionExceptions,
  type ConnectionFailure, type CrossWorkspaceSignal,
} from "@/lib/connections/exceptions.functions";
import { resolveOwner } from "@/lib/connections/ownership-registry";
import { cn } from "@/lib/utils";
import {
  PublicApiInboundCard,
  PublicApiCredentialsPanel,
  PublicApiActivityPanel,
  PublicApiExceptionsPanel,
} from "@/components/admin/public-api-panels";
import { KeyRound } from "lucide-react";
import { Sliders } from "lucide-react";
import { ConnectionShell } from "@/components/admin/connection-shell";
import { ConnectionServicesPanel } from "@/components/admin/connection-services-panel";
import { PlatformDiagnosticsPanel } from "@/components/admin/platform-diagnostics-panel";

const tabSchema = z.object({
  tab: z.enum(["behavior", "overview", "configuration", "credentials", "activity", "exceptions"]).optional(),
});

export const Route = createFileRoute("/_authenticated/app/admin/connections/$connectionId")({
  validateSearch: zodValidator(tabSchema),
  component: ConnectionWorkspace,
});

function ConnectionWorkspace() {
  const { connectionId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const activeTab = search.tab ?? "behavior";

  const get = useServerFn(getConnection);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "connections", "detail", connectionId],
    queryFn: () => get({ data: { connectionId } }),
  });
  const conn = data?.connection as
    | (NonNullable<typeof data>["connection"] & {
        alias?: string | null;
        disabled_at?: string | null;
        archived_at?: string | null;
        deleted_at?: string | null;
      })
    | undefined;
  const isPublicApi = conn?.provider === "public_api";
  // Shipping providers get a Configuration tab that renders (a) the
  // Connection Shell for capability-definition-driven config (Stamps.com
  // today) and (b) the provider-native Services on/off panel for every
  // shipping adapter.
  const isShipping = conn?.provider === "stamps" || conn?.provider === "ups" || conn?.provider === "fedex";
  const showConnectionShell = conn?.provider === "stamps";
  const showConfiguration = isShipping;

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-6xl">
      {/* Breadcrumb / back */}
      <div>
        <Link
          to="/app/admin/connections"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Connections
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold tracking-tight truncate">
              {isLoading ? "…" : conn?.alias ?? conn?.label ?? "Connection"}
            </h1>
            {conn?.alias && (
              <span className="text-xs text-muted-foreground truncate">
                {conn.label}
              </span>
            )}
            {conn && (
              <>
                <Badge variant="outline" className="text-[10px] uppercase">
                  {PROVIDER_FIELDS[conn.provider]?.label ?? conn.provider}
                </Badge>
                <Badge variant="outline" className="text-[10px] uppercase">{conn.env}</Badge>
                <Badge variant={conn.status === "active" ? "default" : conn.status === "error" ? "destructive" : "secondary"}>
                  {conn.status}
                </Badge>
              </>
            )}
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Define exactly how CairnOS runs this customer's business on this connection.
          </p>
        </div>
      </div>

      {/* Reserved Recommendations slot (renders nothing until populated) */}
      <RecommendationsSlot />

      <Tabs
        value={activeTab}
        onValueChange={(v) => navigate({ search: { tab: v as typeof activeTab }, replace: true })}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="behavior" className="gap-1.5">
            <Settings2 className="h-3.5 w-3.5" /> Behavior
          </TabsTrigger>
          <TabsTrigger value="overview" className="gap-1.5">
            <Info className="h-3.5 w-3.5" /> Overview
          </TabsTrigger>
          {showConfiguration && (
            <TabsTrigger value="configuration" className="gap-1.5">
              <Sliders className="h-3.5 w-3.5" /> Configuration
            </TabsTrigger>
          )}
          {isPublicApi && (
            <TabsTrigger value="credentials" className="gap-1.5">
              <KeyRound className="h-3.5 w-3.5" /> Credentials
            </TabsTrigger>
          )}
          <TabsTrigger value="activity" className="gap-1.5">
            <ActivityIcon className="h-3.5 w-3.5" /> Activity
          </TabsTrigger>
          <TabsTrigger value="exceptions" className="gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Exceptions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="behavior" className="space-y-3">
          <BehaviorTab />
        </TabsContent>

        <TabsContent value="overview" className="space-y-4">
          {isPublicApi && <PublicApiInboundCard />}
          <OverviewTab connection={conn} loading={isLoading} />
        </TabsContent>

        {showConfiguration && (
          <TabsContent value="configuration" className="space-y-4">
            {showConnectionShell && <ConnectionShell connectionId={connectionId} />}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold">Services offered on this Connection</h3>
                    <p className="text-xs text-muted-foreground">
                      Turn off services this Connection should never rate-shop or purchase. Disabled services are hidden from Rate Shop and the pack station Service dropdown.
                    </p>
                  </div>
                </div>
                <ConnectionServicesPanel connectionId={connectionId} provider={conn!.provider} />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isPublicApi && (
          <TabsContent value="credentials">
            <PublicApiCredentialsPanel
              connectionId={connectionId}
              tenantId={(conn as { tenant_id?: string } | undefined)?.tenant_id ?? ""}
            />
          </TabsContent>
        )}

        <TabsContent value="activity">
          {isPublicApi
            ? <PublicApiActivityPanel connectionId={connectionId} />
            : <ActivityTab connectionId={connectionId} />}
        </TabsContent>

        <TabsContent value="exceptions">
          {isPublicApi
            ? <PublicApiExceptionsPanel connectionId={connectionId} />
            : <ExceptionsTab connectionId={connectionId} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * Behavior tab — extensible framework. Renders the registry generically. *
 * ---------------------------------------------------------------------- */

function BehaviorTab() {
  const { connectionId } = Route.useParams();
  const listFn = useServerFn(listConnectionBehavior);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "connections", "behavior", connectionId],
    queryFn: () => listFn({ data: { connectionId } }),
  });
  const resolvedById = useMemo(() => {
    const m = new Map<string, ResolvedBehavior>();
    for (const r of data?.settings ?? []) m.set(r.controlId, r);
    return m;
  }, [data]);

  const groups = BEHAVIOR_REGISTRY.filter((g) => g.availability === "available");
  const plannedCount = BEHAVIOR_REGISTRY.length - groups.length;
  return (
    <div className="space-y-3">
      <div className="rounded-lg border bg-muted/20 p-3 text-xs text-muted-foreground">
        Behavior is organized around business outcomes. New groups and controls
        will appear here as CairnOS evolves — without redesigning this page.
      </div>
      <div className="space-y-2">
        {groups.map((group) => (
          <BehaviorGroupCard
            key={group.id}
            group={group}
            connectionId={connectionId}
            resolvedById={resolvedById}
            loading={isLoading}
          />
        ))}
      </div>
      {plannedCount > 0 && (
        <p className="pt-1 text-[11px] text-muted-foreground">
          More behavior groups will appear here as additional capabilities are introduced.
        </p>
      )}
    </div>
  );
}

function BehaviorGroupCard({
  group, connectionId, resolvedById, loading,
}: {
  group: BehaviorGroup;
  connectionId: string;
  resolvedById: Map<string, ResolvedBehavior>;
  loading: boolean;
}) {
  const available = group.availability === "available";
  const [open, setOpen] = useState(available);
  const customized = group.controls.filter(
    (c) => resolvedById.get(c.id)?.source === "override",
  ).length;

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
          >
            {open ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">{group.label}</span>
                {!available && (
                  <Badge variant="outline" className="text-[10px] uppercase">
                    Coming soon
                  </Badge>
                )}
                {available && customized > 0 && (
                  <Badge variant="secondary" className="text-[10px]">
                    {customized} customized
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{group.description}</p>
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-4 pl-11 pr-4">
            {group.controls.length === 0 ? (
              <EmptyGroupState available={available} groupLabel={group.label} />
            ) : (
              <div className="space-y-2">
                {group.controls.map((c) => (
                  <ControlRow
                    key={c.id}
                    control={c}
                    connectionId={connectionId}
                    resolved={resolvedById.get(c.id)}
                    loading={loading}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function EmptyGroupState({ available, groupLabel }: { available: boolean; groupLabel: string }) {
  return (
    <div className="rounded border border-dashed p-3 text-xs text-muted-foreground">
      {available
        ? `Controls for ${groupLabel} land in an upcoming pass. The framework is in place; behavior knobs will appear here without further redesign.`
        : `${groupLabel} is reserved. When controls are introduced, they will appear here automatically.`}
    </div>
  );
}

function ControlRow({
  control, connectionId, resolved, loading,
}: {
  control: BehaviorControl;
  connectionId: string;
  resolved: ResolvedBehavior | undefined;
  loading: boolean;
}) {
  const qc = useQueryClient();
  const setFn = useServerFn(setConnectionBehavior);
  const resetFn = useServerFn(resetConnectionBehavior);
  const [whyOpen, setWhyOpen] = useState(false);
  const isOverride = resolved?.source === "override";

  async function persist(value: string | boolean | number | null) {
    try {
      await setFn({ data: { connectionId, controlId: control.id, value } });
      await qc.invalidateQueries({
        queryKey: ["admin", "connections", "behavior", connectionId],
      });
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function reset() {
    try {
      await resetFn({ data: { connectionId, controlId: control.id } });
      await qc.invalidateQueries({
        queryKey: ["admin", "connections", "behavior", connectionId],
      });
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="flex items-start justify-between gap-3 rounded border p-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{control.label}</span>
          {isOverride ? (
            <Badge variant="secondary" className="text-[10px]">Customized</Badge>
          ) : (
            <Badge variant="outline" className="text-[10px]">Default</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{control.help}</p>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setWhyOpen(true)}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <HelpCircle className="h-3 w-3" /> Why
          </button>
          {resolved?.updatedAt && (
            <span className="text-[11px] text-muted-foreground">
              · Last changed {new Date(resolved.updatedAt).toLocaleString()}
            </span>
          )}
          {isOverride && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" /> Reset to default
            </button>
          )}
        </div>
      </div>
      <div className="shrink-0">
        {loading || !resolved ? (
          <Skeleton className="h-8 w-28" />
        ) : control.kind === "toggle" ? (
          <Switch
            checked={!!resolved.value}
            onCheckedChange={(v) => persist(v)}
          />
        ) : control.kind === "select" ? (
          <Select
            value={String(resolved.value)}
            onValueChange={(v) => persist(v)}
          >
            <SelectTrigger className="h-8 w-56 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {control.options.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          // JSON controls (shipping routing lists) — dedicated operator editor on the roadmap.
          <span className="text-[11px] text-muted-foreground italic">Configured via API · UI editor coming soon</span>
        )}
      </div>
      <WhySheet
        open={whyOpen}
        onOpenChange={setWhyOpen}
        connectionId={connectionId}
        controlId={control.id}
        controlLabel={control.label}
      />
    </div>
  );
}

function WhySheet({
  open, onOpenChange, connectionId, controlId, controlLabel,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  connectionId: string;
  controlId: string;
  controlLabel: string;
}) {
  const explainFn = useServerFn(explainConnectionBehavior);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "connections", "explain", connectionId, controlId],
    queryFn: () => explainFn({ data: { connectionId, controlId } }),
    enabled: open,
  });
  const ex = data?.explanation;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{controlLabel}</SheetTitle>
          <SheetDescription>Why CairnOS behaves this way today.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4 text-sm">
          {isLoading || !ex ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : (
            <>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Decision</div>
                <p className="font-medium">{ex.headline}</p>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Reason</div>
                <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                  {ex.detail.map((d, i) => (<li key={i}>{d}</li>))}
                </ul>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Source</div>
                <p className="text-muted-foreground">{ex.source}</p>
              </div>
              {ex.alternatives.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Alternatives</div>
                  <ul className="space-y-1 mt-1">
                    {ex.alternatives.map((a) => (
                      <li key={a.key} className="text-xs">
                        <span className="font-medium">{a.label}</span>
                        <span className="text-muted-foreground"> — {a.rationale}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {ex.provenance.notes && (
                <p className="text-[11px] text-muted-foreground">{ex.provenance.notes}</p>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* -------- Overview & Coming Soon ---------------------------------------- */

function OverviewTab({
  connection, loading,
}: {
  connection: {
    provider: string;
    env: string;
    status: string;
    health_message: string | null;
    last_health_at: string | null;
    tenant_id?: string;
    alias?: string | null;
    label?: string;
  } | undefined;
  loading: boolean;
}) {
  const { connectionId } = Route.useParams();
  const eventsFn = useServerFn(listIntegrationEvents);
  const { data: ev } = useQuery({
    queryKey: ["admin", "connections", "overview-events", connectionId],
    queryFn: () => eventsFn({ data: { connectionId } }),
    enabled: !!connection,
  });
  const events = (ev?.events ?? []) as ConnectionEvent[];
  const last24 = events.filter(
    (e) => Date.now() - new Date(e.created_at).getTime() < 24 * 60 * 60 * 1000,
  );
  const inboundCount = last24.filter((e) => e.direction === "inbound").length;
  const outboundCount = last24.filter((e) => e.direction === "outbound").length;
  const errorCount = last24.filter(
    (e) => e.error || (e.http_status != null && e.http_status >= 400),
  ).length;

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!connection) return <p className="text-sm text-muted-foreground">Connection not found.</p>;
  const isCarrier = PROVIDER_CATEGORY[connection.provider] === "carrier";
  return (
    <div className="space-y-4">
      <ConnectionAdminControls
        connectionId={connectionId}
        provider={connection.provider}
        alias={connection.alias ?? null}
        isCarrier={isCarrier}
        tenantId={connection.tenant_id ?? ""}
      />
      <ConnectionLifecycleControls
        connectionId={connectionId}
        disabledAt={(connection as { disabled_at?: string | null }).disabled_at ?? null}
        archivedAt={(connection as { archived_at?: string | null }).archived_at ?? null}
        deletedAt={(connection as { deleted_at?: string | null }).deleted_at ?? null}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Card>
          <CardContent className="p-4 space-y-1.5">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">System</div>
            <div className="text-sm font-medium">
              {PROVIDER_FIELDS[connection.provider]?.label ?? connection.provider}
            </div>
            <div className="text-xs text-muted-foreground">Environment: {connection.env}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 space-y-1.5">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Health</div>
            <div className="text-sm font-medium capitalize">{connection.status}</div>
            {connection.health_message && (
              <div className="text-xs text-muted-foreground">{connection.health_message}</div>
            )}
            {connection.last_health_at && (
              <div className="text-[11px] text-muted-foreground">
                Last checked {new Date(connection.last_health_at).toLocaleString()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Last 24 hours
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SummaryStat label="Inbound" value={inboundCount} icon={ArrowDownToLine} />
            <SummaryStat label="Outbound" value={outboundCount} icon={ArrowUpFromLine} />
            <SummaryStat
              label="Errors"
              value={errorCount}
              icon={XCircle}
              tone={errorCount > 0 ? "bad" : "neutral"}
            />
          </div>
        </CardContent>
      </Card>
      <PlatformDiagnosticsPanel
        connection={{
          provider: connection.provider,
          status: connection.status,
          last_health_at: connection.last_health_at,
          health_message: connection.health_message,
        }}
      />
    </div>
  );
}

/* -------- Test / Alias / Certification controls ---------------------- *
 * Sprint 25 · M4.1 — operator-facing actions for any Connection. The
 * Run Certification action is gated to carrier connections (registered
 * with the canonical ShippingConnectionAdapter); alias is presentation-
 * only and never participates in routing.                              */

function ConnectionAdminControls({
  connectionId, provider, alias, isCarrier, tenantId,
}: {
  connectionId: string;
  provider: string;
  alias: string | null;
  isCarrier: boolean;
  tenantId: string;
}) {
  const qc = useQueryClient();
  const testFn = useServerFn(testConnectionFn);
  const aliasFn = useServerFn(updateConnectionAlias);
  const certFn = useServerFn(runCarrierCertification);
  const listCertFn = useServerFn(listCertifications);

  const [aliasDraft, setAliasDraft] = useState(alias ?? "");
  const [busy, setBusy] = useState<"test" | "alias" | "cert" | null>(null);

  const { data: certs, refetch: refetchCerts } = useQuery({
    queryKey: ["admin", "connections", "cert", connectionId],
    queryFn: () => listCertFn(),
    enabled: isCarrier,
    select: (d) =>
      (d?.rows ?? []).filter(
        (r: { connection_id: string | null }) => r.connection_id === connectionId,
      ),
  });

  async function runTest() {
    setBusy("test");
    try {
      const r = await testFn({ data: { connectionId } });
      toast[r.ok ? "success" : "error"](r.message ?? (r.ok ? "Connection OK" : "Failed"));
      qc.invalidateQueries({ queryKey: ["admin", "connections", "detail", connectionId] });
      qc.invalidateQueries({ queryKey: ["admin", "connections", "list"] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function saveAlias() {
    setBusy("alias");
    try {
      const trimmed = aliasDraft.trim();
      await aliasFn({ data: { connectionId, alias: trimmed || null } });
      toast.success(trimmed ? "Alias saved" : "Alias cleared");
      qc.invalidateQueries({ queryKey: ["admin", "connections", "detail", connectionId] });
      qc.invalidateQueries({ queryKey: ["admin", "connections", "list"] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function runCert() {
    if (!tenantId) return toast.error("Tenant unavailable");
    setBusy("cert");
    try {
      // Certification harness validates provider against the supported
      // enum; only carriers with an implemented adapter are accepted.
      if (!["ups", "fedex", "stamps", "dhl"].includes(provider)) {
        toast.error(`Certification not yet wired for ${provider}`);
        return;
      }
      await certFn({
        data: {
          tenantId,
          connectionId,
          carrier: provider as "ups" | "fedex" | "stamps" | "dhl",
          env: "sandbox",
        },
      });
      toast.success("Certification started");
      refetchCerts();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const latestCert = certs?.[0];

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Connection Controls
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Alias (display only)</Label>
            <Input
              value={aliasDraft}
              maxLength={60}
              onChange={(e) => setAliasDraft(e.target.value)}
              placeholder="e.g. UPS — East Coast account"
              className="h-8 text-sm"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Cosmetic. Used in lists only — never in routing, audit, or billing.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={saveAlias}
            disabled={busy !== null || aliasDraft.trim() === (alias ?? "")}
          >
            {busy === "alias" ? "Saving…" : "Save alias"}
          </Button>
          <Button size="sm" onClick={runTest} disabled={busy !== null}>
            {busy === "test" ? "Testing…" : "Test connection"}
          </Button>
          {isCarrier && (
            <Button
              size="sm"
              variant="outline"
              onClick={runCert}
              disabled={busy !== null}
              title="Runs the carrier certification harness against this Connection (sandbox)."
            >
              {busy === "cert" ? "Starting…" : "Run certification"}
            </Button>
          )}
        </div>
        {isCarrier && latestCert && (
          <div className="text-xs text-muted-foreground border-t pt-2">
            Latest certification:{" "}
            <span className="font-medium text-foreground">{latestCert.status}</span>
            {" · "}
            {latestCert.scenarios_passed}/{latestCert.scenarios_total} scenarios passed
            {latestCert.last_error ? ` · ${latestCert.last_error}` : ""}
          </div>
        )}
        {isCarrier && !latestCert && (
          <div className="text-[11px] text-muted-foreground border-t pt-2">
            No certification run yet. Live-credential certification will populate
            <code className="mx-1">carrier_certifications</code>
            once a sandbox or certification credential set is configured.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryStat({
  label, value, icon: Icon, tone = "neutral",
}: {
  label: string;
  value: number;
  icon: typeof CheckCircle2;
  tone?: "neutral" | "bad";
}) {
  const cls = tone === "bad" ? "text-red-600 dark:text-red-400" : "text-foreground";
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        <Icon className={cn("h-3.5 w-3.5", cls)} /> {label}
      </div>
      <div className={cn("mt-1 text-xl font-semibold tabular-nums", cls)}>{value}</div>
    </div>
  );
}

/* -------- Sprint 25 · M5 — Connection lifecycle controls --------------- *
 * Operator actions for the four lifecycle transitions every Connection
 * supports: Enable, Disable, Archive, Delete. Disabled and archived
 * Connections are excluded from new rate shop / label purchase but still
 * resolve for historical void/track/reprint. Delete is blocked when
 * active dependencies (open shipments, active return labels) exist.
 */
function ConnectionLifecycleControls({
  connectionId, disabledAt, archivedAt, deletedAt,
}: {
  connectionId: string;
  disabledAt: string | null;
  archivedAt: string | null;
  deletedAt: string | null;
}) {
  const qc = useQueryClient();
  const enableFn = useServerFn(enableConnection);
  const disableFn = useServerFn(disableConnection);
  const archiveFn = useServerFn(archiveConnection);
  const deleteFn = useServerFn(deleteConnection);
  const auditFn = useServerFn(listConnectionLifecycleAudit);
  const [busy, setBusy] = useState<"enable" | "disable" | "archive" | "delete" | null>(null);

  const state: "active" | "disabled" | "archived" | "deleted" =
    deletedAt ? "deleted" : archivedAt ? "archived" : disabledAt ? "disabled" : "active";

  const { data: audit } = useQuery({
    queryKey: ["admin", "connections", "lifecycle-audit", connectionId],
    queryFn: () => auditFn({ data: { connectionId } }),
  });

  async function run(action: "enable" | "disable" | "archive" | "delete") {
    if (action === "delete") {
      if (!window.confirm("Soft-delete this Connection? It will be hidden from operational lists. Existing shipments and return labels remain resolvable for audit.")) return;
    }
    setBusy(action);
    try {
      const fn = action === "enable" ? enableFn : action === "disable" ? disableFn : action === "archive" ? archiveFn : deleteFn;
      await fn({ data: { connectionId, reason: null } });
      toast.success(`Connection ${action}d`);
      qc.invalidateQueries({ queryKey: ["admin", "connections", "detail", connectionId] });
      qc.invalidateQueries({ queryKey: ["admin", "connections", "lifecycle-audit", connectionId] });
      qc.invalidateQueries({ queryKey: ["admin", "connections", "list"] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const entries = (audit?.entries ?? []) as Array<{ id: string; action: string; reason: string | null; created_at: string }>;

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Lifecycle</div>
          <Badge variant={state === "active" ? "default" : state === "disabled" ? "secondary" : "outline"} className="capitalize">
            {state}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Disabled and archived Connections are excluded from new rate shop and label purchase.
          Existing shipments still resolve through their persisted Connection for audit, tracking, and void.
        </p>
        <div className="flex flex-wrap gap-2">
          {state !== "active" && (
            <Button size="sm" variant="outline" disabled={busy !== null || state === "deleted"} onClick={() => run("enable")}>
              {busy === "enable" ? "Enabling…" : "Enable"}
            </Button>
          )}
          {state === "active" && (
            <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => run("disable")}>
              {busy === "disable" ? "Disabling…" : "Disable"}
            </Button>
          )}
          {state !== "archived" && state !== "deleted" && (
            <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => run("archive")}>
              {busy === "archive" ? "Archiving…" : "Archive"}
            </Button>
          )}
          {state !== "deleted" && (
            <Button size="sm" variant="destructive" disabled={busy !== null} onClick={() => run("delete")}>
              {busy === "delete" ? "Deleting…" : "Delete"}
            </Button>
          )}
        </div>
        {entries.length > 0 && (
          <div className="border-t pt-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Lifecycle audit</div>
            <ul className="text-xs text-muted-foreground space-y-0.5 max-h-32 overflow-auto">
              {entries.slice(0, 6).map((e) => (
                <li key={e.id} className="flex gap-2">
                  <span className="capitalize font-medium text-foreground">{e.action}</span>
                  <span>{new Date(e.created_at).toLocaleString()}</span>
                  {e.reason && <span className="truncate">— {e.reason}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* -------- Activity tab -------------------------------------------------- */

type ConnectionEvent = {
  id: string;
  direction: "inbound" | "outbound" | string;
  kind: string;
  http_status: number | null;
  duration_ms: number | null;
  error: string | null;
  created_at: string;
};

function ActivityTab({ connectionId }: { connectionId: string }) {
  const eventsFn = useServerFn(listIntegrationEvents);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "connections", "activity", connectionId],
    queryFn: () => eventsFn({ data: { connectionId } }),
  });
  const events = (data?.events ?? []) as ConnectionEvent[];

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          No activity yet on this connection.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="rounded-lg border divide-y bg-card">
      {events.map((e) => (
        <ActivityRow key={e.id} event={e} />
      ))}
    </div>
  );
}

function ActivityRow({ event }: { event: ConnectionEvent }) {
  const failed = !!event.error || (event.http_status != null && event.http_status >= 400);
  const Icon = failed
    ? XCircle
    : event.direction === "inbound"
      ? ArrowDownToLine
      : event.direction === "outbound"
        ? ArrowUpFromLine
        : CheckCircle2;
  const tone = failed
    ? "text-red-600 dark:text-red-400"
    : "text-muted-foreground";

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", tone)} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{prettyKind(event.kind, event.direction)}</span>
          {event.http_status != null && (
            <span className="text-[11px] text-muted-foreground">HTTP {event.http_status}</span>
          )}
          {event.duration_ms != null && (
            <span className="text-[11px] text-muted-foreground">{event.duration_ms} ms</span>
          )}
        </div>
        {event.error && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 truncate">{event.error}</p>
        )}
      </div>
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
        <Clock className="h-3 w-3" />
        {new Date(event.created_at).toLocaleString()}
      </div>
    </div>
  );
}

/**
 * Map raw event kinds into operator-language phrases. Unknown kinds fall
 * back to the raw string so we never blank out — but we add new mappings
 * here as we encounter them. This is deliberately a small lookup, not a
 * formatting DSL.
 */
function prettyKind(kind: string, direction: string): string {
  const k = kind.toLowerCase();
  if (k.includes("order") && direction === "inbound") return "Order received";
  if (k.includes("order") && direction === "outbound") return "Order sent";
  if (k.includes("inventory")) return direction === "outbound" ? "Inventory published" : "Inventory update received";
  if (k.includes("shipment") || k.includes("tracking")) return "Shipment / tracking update";
  if (k.includes("ack") || k.includes("acknowledge")) return "Acknowledgement sent";
  if (k.includes("webhook")) return "Webhook received";
  if (k.includes("sync")) return "Sync run";
  return kind;
}

function ComingSoon({ title, body }: { title: string; body: string }) {
  return (
    <Card>
      <CardContent className="p-6 space-y-1.5">
        <div className="text-sm font-medium">{title}</div>
        <p className="text-xs text-muted-foreground max-w-xl">{body}</p>
      </CardContent>
    </Card>
  );
}

/* -------- Exceptions tab ----------------------------------------------- */

function ExceptionsTab({ connectionId }: { connectionId: string }) {
  const listFn = useServerFn(listConnectionExceptions);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "connections", "exceptions", connectionId],
    queryFn: () => listFn({ data: { connectionId } }),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const failures = data?.failures ?? [];
  const signals = data?.signals ?? [];

  if (failures.length === 0 && signals.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          No open exceptions on this connection.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {failures.length > 0 && (
        <section className="space-y-2">
          <SectionHeader
            title="Connection Issues"
            hint="Owned here — review traces or retry."
            count={failures.length}
          />
          <div className="rounded-lg border divide-y bg-card">
            {failures.map((f) => (
              <FailureRow key={f.id} failure={f} />
            ))}
          </div>
        </section>
      )}

      {signals.length > 0 && (
        <section className="space-y-2">
          <SectionHeader
            title="Needs Attention Elsewhere"
            hint="Originated on this connection; another workspace owns resolution."
            count={signals.length}
          />
          <div className="rounded-lg border divide-y bg-card">
            {signals.map((s) => (
              <CrossWorkspaceRow key={s.id} signal={s} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SectionHeader({
  title, hint, count,
}: { title: string; hint: string; count: number }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div>
        <h2 className="text-sm font-medium">{title}</h2>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Badge variant="secondary" className="text-[10px]">{count}</Badge>
    </div>
  );
}

function FailureRow({ failure }: { failure: ConnectionFailure }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <XCircle className="h-4 w-4 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{prettyKind(failure.kind, failure.direction)}</span>
          {failure.http_status != null && (
            <span className="text-[11px] text-muted-foreground">HTTP {failure.http_status}</span>
          )}
        </div>
        {failure.error && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 line-clamp-2">{failure.error}</p>
        )}
      </div>
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
        <Clock className="h-3 w-3" />
        {new Date(failure.created_at).toLocaleString()}
      </div>
    </div>
  );
}

function CrossWorkspaceRow({ signal }: { signal: CrossWorkspaceSignal }) {
  const owner = resolveOwner({ domain: signal.domain, signal_type: signal.signal_type });
  const href = owner.buildHref({
    id: signal.id,
    entity_type: signal.entity_type,
    entity_id: signal.entity_id,
  });
  const tone =
    signal.severity === "critical" || signal.severity === "error"
      ? "text-red-600 dark:text-red-400"
      : signal.severity === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : "text-muted-foreground";

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <AlertTriangle className={cn("h-4 w-4 mt-0.5 shrink-0", tone)} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{prettySignalType(signal.signal_type)}</span>
          <Badge variant="outline" className="text-[10px] uppercase">
            {owner.ownerLabel}
          </Badge>
          <Badge variant="outline" className="text-[10px]">{signal.severity}</Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {owner.hint ?? "Owned by another workspace"}
        </p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <Clock className="h-3 w-3" />
          {new Date(signal.occurred_at).toLocaleString()}
        </span>
        <Button asChild size="sm" variant="outline" className="h-8">
          <Link to={href}>
            Open in {owner.ownerLabel}
            <ExternalLink className="h-3 w-3 ml-1" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

function prettySignalType(t: string): string {
  return t
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

/* -------- Recommendations slot (reserved, renders nothing today) -------- */

function RecommendationsSlot() {
  const recs: { id: string; title: string }[] = [];
  if (recs.length === 0) return null;
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
        <Sparkles className={cn("h-3.5 w-3.5")} /> Recommendations
      </div>
    </div>
  );
}
